import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { clamp, propsToDom, propsToLink } from 'core/helpers';
import { ContainerGrid } from 'components/Container';
import { Panel } from 'components/Panel';
import { Image } from 'components/Image';
import { RichText } from 'components/RichText';
import { Label, Title5 } from 'styles/typography';
import Swiper from './Swiper';
import { rem } from 'styles/utils';
import { Link } from './Link';

// ------------------------------------------------------------
//    CAROUSEL MAIN
// ------------------------------------------------------------
const CarouselPushComponent = ({ primary, items, slice_type, next_slice_type, ...others }) => {
  const { background, label } = primary;
  const hasLabel = !!label?.text;
  const [active, setActive] = useState(0);
  const projects = useMemo(() => items.filter(({ image }) => !!image?.url), [items]);

  const onSwipe = ({ deltaX, deltaY }) => {
    if (Math.abs(deltaX) > 50 && Math.abs(deltaY) < 100) {
      const next = clamp(active + Math.sign(deltaX), 0, projects.length);
      setActive(next);
    }
  };

  return (
    <Panel
      {...propsToDom(others)}
      id={slice_type}
      nextId={next_slice_type}
      background={background}
      style={{ '--length': projects.length, '--active': active }}
    >
      <Swiper tag={ContainerGrid} onSwipe={onSwipe}>
        <Container>
          {hasLabel && <Label>{label.text}</Label>}
          <Slider>
            {projects.map(({ image, item_title, text, link }, index) => {
              const hasTitle = !!item_title?.text;
              const hasText = !!text?.richText?.length > 0;
              const propsLink = link?.url ? propsToLink(link) : {};
              const hasLink = !!propsLink?.to;

              return (
                <Item key={index} isActive={active === index}>
                  <ImageContainer as={hasLink ? Link : null} {...propsLink}>
                    <Image {...image} />
                  </ImageContainer>
                  <Content>
                    {hasTitle && (
                      <Title5 as={hasLink ? Link : 'h3'} {...propsLink}>
                        {item_title.text}
                      </Title5>
                    )}
                    {hasText && <RichText render={text.richText} />}
                  </Content>
                </Item>
              );
            })}
          </Slider>
        </Container>
      </Swiper>
    </Panel>
  );
};

const Slider = styled.div`
  display: flex;
  width: calc(var(--length) * 100%);
  transition: transform 0.7s ${({ theme }) => theme.easings.principle};
  transform: translateX(calc(1 / var(--length) * var(--active) * -100%));
`;

const Content = styled.div``;

const Item = styled.div`
  width: calc((1 / var(--length)) * 100%);

  &:first-child {
    ${Image} {
      border-radius: 8px 0 0 8px;
      overflow: hidden;
    }
  }

  &:last-child {
    ${Image} {
      border-radius: 0 8px 8px 0;
      overflow: hidden;
    }
  }

  ${Title5} {
    display: block;
    margin-top: ${rem(32)};
    margin-bottom: ${rem(16)};
  }

  & > ${Content} {
    transition: opacity 0.4s ${({ theme }) => theme.easings.principle};
    opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  }
`;

const ImageContainer = styled.div``;

const Container = styled.div`
  grid-column: 1 / span 7;

  ${Label} {
    margin-bottom: ${rem(32)};
  }
`;

export const CarouselPushMobile = styled(CarouselPushComponent)`
  position: relative;
  overflow: hidden;
`;
